/**
 * Is this aircraft under somebody's hand RIGHT NOW, and whose?
 *
 * `manual_control_state` is broadcast ORG-WIDE precisely so a second console can
 * learn that an aircraft is already being flown by hand. Until this, its only
 * consumer was the refusal string on the MANUAL key of the SELECTED aircraft —
 * so an operator scanning the picture saw an ordinary track, with the same
 * symbol and the same strip row as everything else, and could send RTH to an
 * aircraft somebody was threading between buildings at 8 m AGL. This is the one
 * fact the unified air picture was not carrying.
 *
 * Freshness is delegated to `manualHoldRefusal`, deliberately: the mark on the
 * picture and the refusal on the key must age out on exactly the same clock, or
 * the picture paints a permanent MANUAL on a track nobody is flying (worse than
 * no mark — it teaches operators to ignore it) or drops it while the lock still
 * holds.
 *
 * NOT filtered by session or by account, unlike the refusal: the picture states
 * what is true of the AIRCRAFT, not what this console is allowed to do with it.
 * An operator's own hold is marked too — it is the same fact, and it is what a
 * second person reading over their shoulder, or the same person on a second
 * screen, needs to see.
 *
 * This adds VISIBILITY ONLY. Nothing here gates a command: ABORT, LAND and
 * RETURN_HOME stay available to every authorised operator in every state, which
 * is the whole reason the mark has to be legible before the press.
 */
import { MANUAL_HOLD_UNKNOWN_OPERATOR, manualHoldRefusal } from './drone-command-availability';
import { refusalHeldBy } from './manual-vector';
import type { ManualControlHold } from '@/types/coordination';

export interface ManualHoldMark {
  /**
   * Who is flying, or null when the broadcast carried no name.
   *
   * Null is "we do not know" — NEVER "nobody". The contract on
   * `ManualControlStatePayload.operator` is explicit: an API restart mid-session
   * loses the name, not the session. Render the nameless case as a hold WITHOUT
   * a name; never as no hold.
   */
  operator: string | null;
  /** `HH:mm` UTC the hold started — the same clock the refusal quotes. */
  since: string;
}

/**
 * The air-picture mark for one aircraft's manual hold, or null when there is
 * nothing to paint (no hold, or a hold that has gone stale).
 */
export function manualHoldMark(
  hold: ManualControlHold | null | undefined,
  now: number = Date.now(),
): ManualHoldMark | null {
  const refusal = manualHoldRefusal({ hold, now });
  if (!refusal) return null;
  // `manualHoldRefusal` substitutes its own placeholder for a missing name so
  // its sentence stays a sentence. The picture has a better answer for that
  // case — the mark itself, with no name beside it — so the placeholder is
  // turned back into the null it stands for.
  return {
    operator: refusal.operator === MANUAL_HOLD_UNKNOWN_OPERATOR ? null : refusal.operator,
    since: refusal.since,
  };
}

/** `MANUAL · EDEN` / `MANUAL` — the map tag. Uppercased by the layer, not here. */
export function manualHoldTag(mark: ManualHoldMark): string {
  return mark.operator ? `MANUAL · ${mark.operator}` : 'MANUAL';
}

/**
 * The full sentence, for a tooltip or a screen reader — the SAME words the
 * MANUAL key refuses with, so an operator who hovers the strip badge and an
 * operator who presses the key read one contract, not two paraphrases of it.
 * The nameless case gets the contract's own placeholder back.
 */
export function manualHoldSentence(mark: ManualHoldMark): string {
  return refusalHeldBy(mark.operator ?? MANUAL_HOLD_UNKNOWN_OPERATOR, mark.since);
}
